import api from './api';

// Keep in sync with the backend MultipartUploadService.PART_SIZE_BYTES (8 MB).
export const PART_SIZE = 8 * 1024 * 1024;
// Below this, a single PUT is fine; above it we switch to resumable multipart.
export const MULTIPART_THRESHOLD = 16 * 1024 * 1024;
const PART_CONCURRENCY = 4;
const PART_RETRIES = 3;

const pct = (loaded, total) => (total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : 0);

/**
 * PUT a body to a presigned S3 URL via XMLHttpRequest.
 * Unlike fetch(), this reports upload progress AND rejects on a non-2xx status (fetch resolves on
 * 403/5xx, which is exactly how failed uploads used to look "successful" and leave content stuck).
 * Resolves with the S3 ETag (needed to assemble multipart uploads).
 */
export function uploadToUrl(url, body, { contentType, onProgress } = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    // Only set Content-Type for single PUTs, where it is part of the signature. Multipart part URLs
    // do not sign it, so we leave it unset to avoid a signature mismatch.
    if (contentType) xhr.setRequestHeader('Content-Type', contentType);
    if (xhr.upload && onProgress) {
      xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded, e.total); };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ etag: xhr.getResponseHeader('ETag') });
      } else {
        reject(new Error(`Upload rejected by storage (HTTP ${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.ontimeout = () => reject(new Error('Upload timed out'));
    xhr.send(body);
  });
}

/** Split a file size into ordered part descriptors {partNumber, start, end}. */
export function planParts(fileSize, partSize = PART_SIZE) {
  const parts = [];
  let n = 1;
  for (let start = 0; start < fileSize; start += partSize) {
    parts.push({ partNumber: n++, start, end: Math.min(start + partSize, fileSize) });
  }
  return parts;
}

async function withRetry(fn, attempts) {
  let lastErr;
  for (let i = 0; i < attempts; i++) {
    try { return await fn(); } catch (e) { lastErr = e; }
  }
  throw lastErr;
}

// Run `worker(item, index)` over items with bounded concurrency.
async function runPool(items, concurrency, worker) {
  let cursor = 0;
  const runners = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    while (cursor < items.length) {
      const idx = cursor++;
      await worker(items[idx], idx);
    }
  });
  await Promise.all(runners);
}

async function multipartUpload(contentId, file, contentType, onProgress) {
  const initRes = await api.post(`/api/v1/content/${contentId}/multipart/init`, null, {
    params: { contentType, parts: Math.ceil(file.size / PART_SIZE) },
  });
  const { s3Key, uploadId, partUrls, partSizeBytes } = initRes.data.data;
  const plan = planParts(file.size, partSizeBytes || PART_SIZE);
  const loaded = new Array(plan.length).fill(0);
  const results = new Array(plan.length);
  const report = () => onProgress?.(pct(loaded.reduce((a, b) => a + b, 0), file.size));

  try {
    await runPool(plan, PART_CONCURRENCY, async (part, idx) => {
      const blob = file.slice(part.start, part.end);
      const { etag } = await withRetry(
        () => uploadToUrl(partUrls[idx], blob, { onProgress: (l) => { loaded[idx] = l; report(); } }),
        PART_RETRIES,
      );
      loaded[idx] = part.end - part.start;
      report();
      results[idx] = { partNumber: part.partNumber, etag };
    });
    await api.post(`/api/v1/content/${contentId}/multipart/complete`, { s3Key, uploadId, parts: results });
    return s3Key;
  } catch (err) {
    // Free the buffered parts in S3 so a failed attempt doesn't linger or bill.
    try { await api.post(`/api/v1/content/${contentId}/multipart/abort`, { s3Key, uploadId }); } catch { /* best effort */ }
    throw err;
  }
}

/**
 * Upload a content file to S3, choosing single-PUT or resumable multipart by size, and return the
 * s3Key. The caller finalizes the DB row via /upload-complete exactly as before.
 * `onProgress` receives an integer percent 0..100.
 */
export async function uploadContentFile(contentId, file, { contentType, onProgress } = {}) {
  const ct = contentType || file.type || 'application/octet-stream';
  if (file.size <= MULTIPART_THRESHOLD) {
    const urlRes = await api.post(`/api/v1/content/${contentId}/upload-url`, null, { params: { contentType: ct } });
    const { uploadUrl, s3Key } = urlRes.data.data;
    await uploadToUrl(uploadUrl, file, { contentType: ct, onProgress: (l, t) => onProgress?.(pct(l, t)) });
    return s3Key;
  }
  return multipartUpload(contentId, file, ct, onProgress);
}

export default { uploadContentFile, uploadToUrl, planParts, PART_SIZE, MULTIPART_THRESHOLD };
